import { useState, useCallback, useEffect, useRef } from 'react';
import type { Arealplan } from '@origo/arealplan';
import { planKey, useArealplanClient } from '@origo/arealplan';
import type { PanelComponentProps } from '../../components/AdminSidePanel';
import { PlanSearch, type PlanFocusRequest } from './PlanSearch';
import { AdminPage } from './components/AdminPage';
import { PlanPickPopup } from './components/PlanPickPopup';
import { useMapViewContext, selectedPolygonSymbol } from '@origo/map-context';
import Graphic from '@arcgis/core/Graphic';
import { isAbortError } from '@arcgis/core/core/promiseUtils';
import { usePlanMetadata } from './hooks/usePlanMetadata';
import { usePlanMapPick, type PlanMapPick } from './hooks/usePlanMapPick';
import { getPlanPublishStatus } from '../filter/filterUtils';

/** Søkevisning: 50 vw. Søk + avansert filter: 70 vw (plass til filter-kolonne). Adminredigering: 75 vw. */
const panelWidth = (vw: number) => Math.floor(window.innerWidth * vw / 100);

interface AdminViewProps extends PanelComponentProps {
    selectedPlan: Arealplan | null;
    onSelectPlan: (plan: Arealplan | null) => void;
}

export function AdminView({ onClose, onPanelWidthChange, eiendommerUrl, selectedPlan, onSelectPlan }: AdminViewProps) {
    const [filterOpen, setFilterOpen] = useState(false);
    const { mapView } = useMapViewContext();
    const arealplanClient = useArealplanClient();
    const { metadata, publishPlan, unpublishPlan, registrerRedigering, updateBeskrivelse, updatePlanregisterlink, updateGjeldendeBestemmelser } = usePlanMetadata();

    const handleSelect = useCallback((plan: Arealplan) => {
        onSelectPlan(plan);
    }, [onSelectPlan]);

    // Bare siste zoom-forespørsel tegnes — et tregt svar for en tidligere valgt
    // plan skal ikke overskrive et nyere valg.
    const zoomRequest = useRef(0);

    const handleZoomTo = useCallback(async (plan: Arealplan) => {
        if (!mapView) return;
        const request = ++zoomRequest.current;
        let geometry: unknown;
        try {
            geometry = await arealplanClient.arealplaner.getGeometry(plan);
        } catch (err) {
            if (request === zoomRequest.current) console.error('Kunne ikke hente plangeometri', err);
            return;
        }
        if (request !== zoomRequest.current || !geometry) return;
        mapView.graphics.removeAll();
        mapView.graphics.add(new Graphic({
            geometry: geometry as __esri.Geometry,
            symbol: selectedPolygonSymbol() as any,
        }));
        // Ny navigasjon (raske kartklikk) avbryter goTo med AbortError — det er
        // forventet. Andre feil skal ikke svelges.
        mapView.goTo(geometry as __esri.Geometry).catch((err) => {
            if (!isAbortError(err)) console.error('Kunne ikke zoome til planen', err);
        });
    }, [mapView, arealplanClient]);

    // Finn plan via kartet: treff vises alltid i en velger ved klikkpunktet —
    // også ved ett treff, så et utilsiktet klikk ikke flytter brukeren.
    const [focusRequest, setFocusRequest] = useState<PlanFocusRequest | null>(null);
    const [mapPick, setMapPick] = useState<PlanMapPick | null>(null);
    const focusNonce = useRef(0);

    // Marker planen i lista og zoom til hele planområdet, så det er tydelig
    // hvilken plan klikket traff.
    const focusPlanFromMap = useCallback((plan: Arealplan) => {
        setMapPick(null);
        setFocusRequest({ key: planKey(plan), nonce: ++focusNonce.current });
        void handleZoomTo(plan);
    }, [handleZoomTo]);

    const handleMapPick = useCallback((pick: PlanMapPick) => {
        setMapPick(pick.plans.length > 0 ? pick : null);
    }, []);

    const closeMapPick = useCallback(() => setMapPick(null), []);

    usePlanMapPick({ mapView, enabled: !selectedPlan, onPick: handleMapPick });

    // Kart-valget gjelder bare denne visningen — nullstill når en plan åpnes, så
    // en gammel forespørsel ikke markerer feil kort når brukeren kommer tilbake.
    useEffect(() => {
        if (!selectedPlan) return;
        setMapPick(null);
        setFocusRequest(null);
    }, [selectedPlan]);

    // Signal width change based on current view + filter state
    useEffect(() => {
        if (selectedPlan) {
            onPanelWidthChange?.(panelWidth(75));
        } else if (filterOpen) {
            onPanelWidthChange?.(panelWidth(70));
        } else {
            onPanelWidthChange?.(panelWidth(50));
        }
    }, [selectedPlan, filterOpen, onPanelWidthChange]);

    if (selectedPlan) {
        const planMeta = metadata.get(selectedPlan.planId);
        const isPublished = getPlanPublishStatus(planMeta) === 'publisert';
        return (
            <AdminPage
                plan={selectedPlan}
                onBack={() => onSelectPlan(null)}
                onClose={onClose}
                onPublish={publishPlan}
                onUnpublish={unpublishPlan}
                onEdited={registrerRedigering}
                onUpdateBeskrivelse={(beskrivelse) => updateBeskrivelse(selectedPlan.planId, beskrivelse, planMeta?.kommunenummer)}
                onUpdatePlanregisterlink={(url) => updatePlanregisterlink(selectedPlan.planId, url, planMeta?.kommunenummer)}
                onUpdateGjeldendeBestemmelser={(url) => updateGjeldendeBestemmelser(selectedPlan.planId, url, planMeta?.kommunenummer)}
                eiendommerUrl={eiendommerUrl}
                isPublished={isPublished}
                sistRedigertDato={planMeta?.sistRedigertDato}
                planMetadata={planMeta}
            />
        );
    }

    return (
        <>
            <PlanSearch
                onSelect={handleSelect}
                onZoomTo={handleZoomTo}
                focusRequest={focusRequest}
                onFilterToggle={setFilterOpen}
                planMetadata={metadata}
            />
            {mapPick && mapView && (
                <PlanPickPopup
                    mapView={mapView}
                    mapPoint={mapPick.mapPoint}
                    plans={mapPick.plans}
                    onChoose={focusPlanFromMap}
                    onClose={closeMapPick}
                />
            )}
        </>
    );
}
